/**
 * Umami custom event tracking utility.
 * Respects GDPR cookie consent before sending events.
 */
import { VARIANT_SPECS, VARIANT_SPECS_BY_CODE, toGameVariant } from '@draughts/shared';
import type { VariantCode } from '@draughts/shared';
import { isAnalyticsAllowed } from './cookie-consent';
import { markFirst, recordVariantPlayed } from './analytics-local';
import { getCampagna } from './campagna';

declare global {
  interface Window {
    umami?: {
      /*
       * Due forme, ed e' la libreria ad averle: col NOME scrive un evento
       * personalizzato, con una FUNZIONE che trasforma il payload scrive una
       * visita vera — a patto che il payload non porti `name`. La seconda e'
       * l'unico modo di far esistere le nostre schermate in Umami senza
       * riscrivere il routing dell'app (vedi `trackPageView`).
       */
      track: {
        (event: string, data?: Record<string, string | number | boolean>): void;
        (trasforma: (payload: Record<string, unknown>) => Record<string, unknown>): void;
      };
    };
  }
}

function track(event: string, data?: Record<string, string | number | boolean>): void {
  if (!isAnalyticsAllowed() || !window.umami) return;
  try {
    window.umami.track(event, data);
  } catch {
    // Silent fail - analytics should never break the app
  }
}

/**
 * Canonical variant properties for analytics. The same variant reaches the
 * trackers in three legacy forms — enum UPPERCASE ('INTERNATIONAL'),
 * lowercase-long ('international'), and 3-letter code ('int') — so without
 * normalization Umami breakdowns fragment across 27×N tokens. This collapses
 * ALL three into one canonical set, keyed to the shared Master Matrix:
 *  - `variant`: lowercase-long, equal to LOWER(games.variant) server-side, so
 *    Umami breakdowns line up 1:1 with the exact SQL in docs/ANALYTICS.md.
 *  - `variantCode`: the stable 3-letter code (compact grouping).
 *  - `boardSize`: 8/10/12/14, to bucket the 27 variants by board.
 * Exported for unit testing.
 */
export function variantProps(input: string): { variant: string; variantCode: string; boardSize: number } {
  const spec =
    VARIANT_SPECS_BY_CODE[input.toLowerCase() as VariantCode] ??
    VARIANT_SPECS[toGameVariant(input)];
  return {
    variant: spec.variant.toLowerCase(),
    variantCode: spec.code,
    boardSize: spec.board.size,
  };
}

// ── Auth & Conversion ──

export function trackRegistration(): void {
  track('registration');
}

export function trackLogin(method: 'credentials' | 'guest'): void {
  track('login', { method });
}

export function trackGuestUpgrade(): void {
  track('guest-upgrade');
}

/**
 * Chi ha chiesto di giocare senza avere un conto.
 *
 * `source` dice da QUALE porta: la hero della landing o la prima riga del foglio
 * di «Gioca». Le due porte dicono la stessa parola — «Gioca subito» — e fino a
 * poco fa facevano cose diverse; senza la provenienza, il totale non si sa in
 * quale delle due si perde. Gli eventi vecchi non hanno la proprieta' e in Umami
 * restano dentro il totale.
 *
 * Solo per il tentativo: `trackLogin('guest')` misura l'ingresso RIUSCITO, e il
 * rapporto fra i due da' gratis il tasso di rifiuto del limitatore.
 */
export function trackGuestPlayClicked(source?: 'hero' | 'foglio-gioca'): void {
  track('guest-play-clicked', source ? { source } : undefined);
}

export function trackGuestOverlayConfirmed(variant: string, tc: string): void {
  track('guest-overlay-confirmed', { ...variantProps(variant), tc });
}

export function trackGuestOverlayDismissed(): void {
  track('guest-overlay-dismissed');
}

export function trackEmailVerified(): void {
  track('email-verified');
}

export function trackForgotPasswordRequested(): void {
  track('forgot-password-requested');
}

export function trackPasswordResetCompleted(): void {
  track('password-reset-completed');
}

export function trackLoginFailed(reason: string): void {
  track('login-failed', { reason });
}

export function trackRegistrationFailed(reason: string): void {
  track('registration-failed', { reason });
}

// ── Gameplay ──

export function trackGameStarted(opts: {
  variant: string;
  timeControl: string;
  mode: 'ai' | 'multiplayer' | 'hotseat';
  aiLevel?: number;
  /** Win-condition modifier (normal | breakthrough | antidraughts); defaults to 'normal'. */
  ruleMode?: string;
}): void {
  track('game-started', {
    ...variantProps(opts.variant),
    timeControl: opts.timeControl,
    mode: opts.mode,
    ruleMode: opts.ruleMode ?? 'normal',
    ...(opts.aiLevel !== undefined && { aiLevel: opts.aiLevel }),
  });
}

export function trackGameCompleted(opts: {
  variant: string;
  timeControl: string;
  result: 'win' | 'loss' | 'draw';
  mode: 'ai' | 'multiplayer' | 'hotseat';
  moves: number;
  durationSeconds: number;
  /** Win-condition modifier (normal | breakthrough | antidraughts); defaults to 'normal'. */
  ruleMode?: string;
}): void {
  track('game-completed', {
    ...variantProps(opts.variant),
    timeControl: opts.timeControl,
    result: opts.result,
    mode: opts.mode,
    moves: opts.moves,
    durationSeconds: opts.durationSeconds,
    ruleMode: opts.ruleMode ?? 'normal',
  });
}

export function trackVariantSelected(variant: string): void {
  track('variant-selected', variantProps(variant));
}

// ── Engagement ──

export function trackArenaJoin(arenaId: string, variant: string): void {
  track('arena-join', { arenaId, ...variantProps(variant) });
}

export function trackArenaLeave(arenaId: string): void {
  track('arena-leave', { arenaId });
}

export function trackTournamentJoin(tournamentId: string, variant: string): void {
  track('tournament-join', { tournamentId, ...variantProps(variant) });
}

export function trackTournamentLeave(tournamentId: string): void {
  track('tournament-leave', { tournamentId });
}

export function trackChallengeSent(
  variant: string,
  timeControl: string,
  extra?: { ruleMode?: string; opening?: string; rated?: boolean },
): void {
  track('challenge-sent', {
    ...variantProps(variant),
    timeControl,
    ...(extra?.ruleMode && { ruleMode: extra.ruleMode }),
    ...(extra?.opening && { opening: extra.opening }),
    ...(extra?.rated !== undefined && { rated: extra.rated }),
  });
}

export function trackRematchRequested(): void {
  track('rematch-requested');
}

export function trackProfileVisited(isOwnProfile: boolean): void {
  track('profile-visited', { isOwnProfile });
}

/**
 * Segnalazione inviata DAL PROFILO. Evento distinto da
 * `chat-report-submitted`: le due hanno motivi diversi e provenienze diverse, e
 * sommarle nasconderebbe l'unica domanda interessante — da dove arriva la
 * moderazione.
 */
export function trackPlayerReportSubmitted(reason: string): void {
  track('player-report-submitted', { reason });
}

// ── Navigation ──

/**
 * La schermata corrente, scritta come VISITA e non come evento.
 *
 * L'app intera vive su `/`: il routing e' uno stato interno, quindi Umami
 * vedeva un URL solo per ventotto schermate. Da li' una frequenza di rimbalzo
 * che non misura la noia — misura che chi gioca resta su `/` — e una durata
 * media senza senso. Passando a `track` una FUNZIONE che riscrive il payload,
 * e lasciandolo senza `name`, il server lo registra come visita di
 * `/<schermata>`: le schermate esistono, e non si tocca una riga di routing.
 *
 * ⚠️ Smette quindi di esistere l'evento personalizzato `page-view`: i report
 * che lo nominavano vanno rifatti sulle visite (vedi docs/ANALYTICS.md). Lo
 * storico resta dov'e', semplicemente non cresce piu'.
 */
export function trackPageView(page: string): void {
  if (!isAnalyticsAllowed() || !window.umami) return;
  try {
    window.umami.track((payload: Record<string, unknown>) => ({ ...payload, url: `/${page}` }));
  } catch {
    // Silent fail - analytics should never break the app
  }
}

// ── Cookie Consent ──

export function trackConsentResponse(accepted: boolean): void {
  // Note: this uses raw umami.track directly since consent was JUST given/denied
  // and isAnalyticsAllowed() may not reflect the new state yet for "accepted"
  if (!window.umami) return;
  try {
    window.umami.track('consent-response', { accepted });
  } catch {
    // Silent fail
  }
}

// ── Settings ──

export function trackLanguageChanged(language: string): void {
  track('language-changed', { language });
}

// ── Game Setup ──

export function trackAiDifficultySelected(difficulty: string): void {
  track('ai-difficulty-selected', { difficulty });
}

export function trackTimeControlSelected(timeControl: string): void {
  track('time-control-selected', { timeControl });
}

// ── Game Actions ──

export function trackGameResigned(mode: 'ai' | 'multiplayer'): void {
  track('game-resigned', { mode });
}

export function trackDrawOffered(): void {
  track('draw-offered');
}

export function trackDrawAccepted(): void {
  track('draw-accepted');
}

export function trackDrawDeclined(): void {
  track('draw-declined');
}

export function trackReviewModeUsed(): void {
  track('review-mode-used');
}

export function trackGameTimeout(variant: string, tc: string, result: 'win' | 'loss'): void {
  track('game-timeout', { ...variantProps(variant), tc, result });
}

export function trackGameAbandoned(variant: string, result: 'win' | 'loss'): void {
  track('game-abandoned', { ...variantProps(variant), result });
}

export function trackGameReconnected(variant: string): void {
  track('game-reconnected', variantProps(variant));
}

export function trackRematchAccepted(): void {
  track('rematch-accepted');
}

export function trackRematchDeclined(): void {
  track('rematch-declined');
}

// ── Arena ──

export function trackArenaGameStarted(arenaId: string, variant: string): void {
  track('arena-game-started', { arenaId, ...variantProps(variant) });
}

export function trackArenaGameCompleted(arenaId: string, variant: string, result: 'win' | 'loss' | 'draw'): void {
  track('arena-game-completed', { arenaId, ...variantProps(variant), result });
}

export function trackArenaPaused(arenaId: string): void {
  track('arena-paused', { arenaId });
}

export function trackArenaResumed(arenaId: string): void {
  track('arena-resumed', { arenaId });
}

// ── Tournament ──

export function trackTournamentRoundStarted(tournamentId: string, round: number): void {
  track('tournament-round-started', { tournamentId, round });
}

export function trackTournamentByeReceived(tournamentId: string): void {
  track('tournament-bye-received', { tournamentId });
}

// ── Lobby (public FIFO tables) ──
//
// Replaces the old matchmaking-* events. There is no 'found' and no 'timeout'
// any more: a quick press either seats you straight away or opens a table, and
// a table waits until somebody sits or it expires.

export function trackTableCreated(variant: string, timeControl: string): void {
  track('table-created', { ...variantProps(variant), timeControl });
}

export function trackTableCancelled(): void {
  track('table-cancelled');
}

export function trackTableSeated(variant: string, tc: string): void {
  track('table-seated', { ...variantProps(variant), tc });
}

// ── Challenges ──

export function trackChallengeAccepted(): void {
  track('challenge-accepted');
}

export function trackChallengeDeclined(): void {
  track('challenge-declined');
}

/** A shareable challenge link was generated (the friend may never open it). */
export function trackChallengeLinkCreated(variant: string, timeControl: string): void {
  track('challenge-link-created', { ...variantProps(variant), timeControl });
}

/** A friend opened a challenge link and accepted it. */
export function trackChallengeLinkAccepted(variant?: string): void {
  track('challenge-link-accepted', variant ? variantProps(variant) : {});
}

// ── Social ──

export function trackChatMessageSent(): void {
  track('chat-message-sent');
}

/** A player reported their opponent for chat abuse. Reason only — no content. */
export function trackChatReportSubmitted(reason: string): void {
  track('chat-report-submitted', { reason });
}

// ── Leaderboard ──

export function trackLeaderboardViewed(variant: string, timeControl: string): void {
  track('leaderboard-viewed', { ...variantProps(variant), timeControl });
}

export function trackLeaderboardFiltered(variant: string, tc: string, period: string): void {
  track('leaderboard-filtered', { ...variantProps(variant), tc, period });
}

/**
 * Evento a se' per la finestra dei «piu' attivi». Non riusa lo slot `period` di
 * `trackLeaderboardFiltered`: li' dentro oggi ci finisce il nome della tab, e
 * metterci il periodo vero cambierebbe il significato di una serie gia'
 * raccolta. `days` null = di sempre.
 */
export function trackLeaderboardPeriodChanged(variant: string, days: number | null): void {
  track('leaderboard-period-changed', { ...variantProps(variant), days: days === null ? 'all' : String(days) });
}

// ── Navigation & UI ──

export function trackAuthModalOpened(tab: string): void {
  track('auth-modal-opened', { tab });
}

export function trackAuthModalTabSwitched(to: string): void {
  track('auth-modal-tab-switched', { to });
}

export function trackSettingsPanelOpened(): void {
  track('settings-panel-opened');
}

export function trackProfileTabSwitched(tab: string): void {
  track('profile-tab-switched', { tab });
}

/** The folder filter's UI sentinels: app constants, safe to send verbatim. */
const FOLDER_FILTER_SENTINELS = new Set(['all', 'saved', 'unsorted']);

export function trackProfileGamesFiltered(filterType: string, value: string): void {
  // A folder id is a row of game_folders with a user_id on it: a per-user
  // pseudonymous identifier that would let Umami cluster events by account.
  // (Unlike an arena id, which names a shared public resource.) Anything that
  // isn't one of the three sentinels collapses to 'folder'; the folder NAME —
  // free text, up to 60 chars — never leaves the device at all.
  // Normalised here rather than at the call site, the same way variantProps does,
  // so the next caller can't forget.
  const safe = filterType === 'folder' && !FOLDER_FILTER_SENTINELS.has(value) ? 'folder' : value;
  track('profile-games-filtered', { filterType, value: safe });
}

/**
 * All four games filters reset at once, from the bar's × or the empty state.
 * Its own event rather than four `profile-games-filtered` calls with value
 * 'all': those would quadruple the count per reset and swamp the `value`
 * breakdown that event exists to answer. No properties — there is nothing to
 * say beyond "it happened".
 */
export function trackProfileGamesFiltersCleared(): void {
  track('profile-games-filters-cleared');
}

// ── Connection ──

export function trackSocketDisconnected(reason: string): void {
  track('socket-disconnected', { reason });
}

export function trackSocketReconnected(): void {
  track('socket-reconnected');
}

// ── Account ──

export function trackPasswordChanged(): void {
  track('password-changed');
}

export function trackAccountDeleted(): void {
  track('account-deleted');
}

export function trackDataExported(): void {
  track('data-exported');
}

export function trackLogout(): void {
  track('logout');
}

// ── Dashboard ──

export function trackDashboardViewed(): void {
  track('dashboard-viewed');
}

export function trackDashboardVariantSwitched(from: string, to: string): void {
  track('dashboard-variant-switched', { from, to });
}

export function trackDashboardTimeControlSwitched(from: string, to: string): void {
  track('dashboard-time-control-switched', { from, to });
}


export function trackDashboardCtaVsAI(variant: string, difficulty: string): void {
  track('dashboard-cta-vs-ai', { ...variantProps(variant), difficulty });
}

export function trackDashboardCtaChallenge(): void {
  track('dashboard-cta-challenge');
}

export function trackDashboardChallengeAccepted(challengeId: string): void {
  track('dashboard-challenge-accepted', { challengeId });
}

export function trackDashboardChallengeDeclined(challengeId: string): void {
  track('dashboard-challenge-declined', { challengeId });
}

export function trackDashboardReplayClicked(gameId: string): void {
  track('dashboard-replay-clicked', { gameId });
}

export function trackDashboardLiveEventClicked(eventId: string, type: 'tournament' | 'arena'): void {
  track('dashboard-live-event-clicked', { eventId, type });
}

export function trackDashboardPuzzleClicked(): void {
  track('dashboard-puzzle-clicked');
}

export function trackDashboardStreakPlay(): void {
  track('dashboard-streak-play');
}

export function trackDashboardStreakPuzzle(): void {
  track('dashboard-streak-puzzle');
}

export function trackDashboardGoalPlay(): void {
  track('dashboard-goal-play');
}

// ── Puzzles ──

export function trackPuzzleStarted(variant: string, rated: boolean): void {
  track('puzzle-started', { ...variantProps(variant), rated });
}

export function trackPuzzleSolved(opts: {
  variant: string;
  rated: boolean;
  streak: number;
  ratingChange?: number | null;
}): void {
  track('puzzle-solved', {
    ...variantProps(opts.variant),
    rated: opts.rated,
    streak: opts.streak,
    ...(opts.ratingChange != null && { ratingChange: opts.ratingChange }),
  });
}

export function trackPuzzleFailed(variant: string, reason: 'give-up' | 'wrong'): void {
  track('puzzle-failed', { ...variantProps(variant), reason });
}

export function trackPuzzleHintUsed(variant: string): void {
  track('puzzle-hint-used', variantProps(variant));
}

// ── Feature adoption (first use — anonymous, localStorage-gated) ──

/**
 * Emit `event` only the first time `firstKey` is seen on this browser. The
 * consent/umami guard runs BEFORE markFirst so the "first" flag is consumed
 * only when the event is actually sent (a user who later grants consent still
 * gets their genuine first-use event).
 */
function trackOnce(firstKey: string, event: string, data?: Record<string, string | number | boolean>): void {
  if (!isAnalyticsAllowed() || !window.umami) return;
  if (!markFirst(firstKey)) return;
  track(event, data);
}

export function trackFirstGameEver(mode: 'ai' | 'multiplayer' | 'hotseat'): void {
  trackOnce('game-ever', 'first-game-ever', { mode });
}

export function trackFirstPuzzle(): void {
  trackOnce('puzzle', 'first-puzzle');
}

export function trackFirstTournament(): void {
  trackOnce('tournament', 'first-tournament');
}

export function trackFirstArena(): void {
  trackOnce('arena', 'first-arena');
}

// ── Variant breadth (per-browser proxy for "how many variants does a user play") ──

/**
 * Records this variant in the per-browser set and emits `variant-breadth` with
 * the running distinct count. Also emits `variant-first-play` the first time a
 * given variant is seen (derived from the same set, so no extra bookkeeping).
 * Fire from every game-started chokepoint. Anonymous: only counts/flags leave
 * the device — never the variant history or an identity. The EXACT per-user
 * answer comes from the server-side SQL (docs/ANALYTICS.md).
 */
export function trackVariantBreadth(variant: string): void {
  if (!isAnalyticsAllowed() || !window.umami) return;
  const props = variantProps(variant);
  const { distinctCount, isNew } = recordVariantPlayed(props.variantCode);
  track('variant-breadth', { variant: props.variant, distinctCount, isNewVariant: isNew });
  if (isNew) {
    track('variant-first-play', props);
  }
}

// ── Preferences / appearance ──

export function trackPreferredVariantChanged(variant: string): void {
  track('preferred-variant-changed', variantProps(variant));
}

export function trackCadenceChanged(cadence: string): void {
  track('cadence-changed', { cadence });
}

export function trackDailyGoalChanged(target: number): void {
  track('daily-goal-changed', { target });
}

export function trackBoardThemeChanged(theme: string): void {
  track('board-theme-changed', { theme });
}

export function trackPieceColorThemeChanged(theme: string): void {
  track('piece-color-theme-changed', { theme });
}

export function trackPieceShapeChanged(shape: string): void {
  track('piece-shape-changed', { shape });
}

export function trackPieceStyleChanged(style: string): void {
  track('piece-style-changed', { style });
}

export function trackKingIconChanged(icon: string): void {
  track('king-icon-changed', { icon });
}

/** La corona 3D ha un asse suo: mescolarla all'emblema renderebbe illeggibili entrambi. */
export function trackCrownTypeChanged(type: string): void {
  track('crown-type-changed', { type });
}

export function trackPieceMaterialChanged(material: string): void {
  track('piece-material-changed', { material });
}

export function trackBoardMaterialChanged(material: string): void {
  track('board-material-changed', { material });
}

/** Etichette sulle caselle: 'none' | 'numeric' | 'algebraic'. */
export function trackBoardNotationChanged(notation: string): void {
  track('board-notation-changed', { notation });
}

/**
 * `category` distingue i tre canali audio indipendenti (mosse / poco tempo / notifiche).
 * L'evento resta uno solo: sono la stessa decisione dell'utente su tre oggetti diversi,
 * e tenerli insieme lascia leggere "quanti spengono l'audio" senza sommare tre serie.
 */
export function trackSoundToggled(category: 'move' | 'low-time' | 'notification', enabled: boolean): void {
  track('sound-toggled', { category, enabled });
}

export function trackVibrationToggled(enabled: boolean): void {
  track('vibration-toggled', { enabled });
}

export function trackMoveHintsToggled(enabled: boolean): void {
  track('move-hints-toggled', { enabled });
}

export function trackPremoveToggled(enabled: boolean): void {
  track('premove-toggled', { enabled });
}

/** Tema della cornice dell'app (chiaro/scuro). Non e' un evento di variante. */
export function trackUiThemeChanged(theme: string): void {
  track('ui-theme-changed', { theme });
}

/** «Non disturbare», acceso o spento. Si manda a scrittura CONFERMATA. */
export function trackDoNotDisturbToggled(enabled: boolean): void {
  track('do-not-disturb-toggled', { enabled });
}

// ── Session ──

/**
 * Fire `session-start` once per browser tab. The consent/umami guard runs first
 * so the per-tab flag is consumed only when the event is actually sent; call it
 * from the page-view effect so it also fires correctly the moment a first-time
 * visitor grants consent and navigates (rather than being lost at boot).
 */
export function trackSessionStart(): void {
  if (!isAnalyticsAllowed() || !window.umami) return;
  try {
    if (sessionStorage.getItem('draughts-analytics-session')) return;
    sessionStorage.setItem('draughts-analytics-session', '1');
  } catch {
    // sessionStorage unavailable — fall through and track without dedup
  }
  track('session-start');
}

// ── PWA ──

/**
 * The "open in app" / "install" banner shown to someone who landed here from a
 * shared link outside the installed app. `context` is the BrowserContext that
 * triggered it (see utils/in-app-browser.ts) — without it the numbers can't
 * separate "trapped in WhatsApp's WebView" from "in a normal browser, no app yet",
 * which are two different problems with two different fixes.
 */
export function trackOpenInAppShown(context: string): void {
  track('open-in-app-shown', { context });
}

export function trackOpenInAppTapped(context: string): void {
  track('open-in-app-tapped', { context });
}

export function trackInstallPromptAccepted(): void {
  track('install-prompt-accepted');
}

/**
 * La card «Installa l'app» in fondo alla dashboard (InstallAppCard).
 *
 * Separata dagli eventi `open-in-app-*` di proposito: quelli misurano chi e'
 * atterrato da un link condiviso, questa misura chi usa l'app ogni giorno dal
 * browser — due pubblici diversi, con tassi di conversione che non ha senso
 * sommare. `platform` distingue i due rami della card (prompt nativo vs
 * istruzioni per sistema operativo), senza cui i numeri non dicono quale dei
 * due converte.
 */
export function trackInstallCardShown(platform: string): void {
  track('install-card-shown', { platform });
}

/** Quale sistema operativo ha scelto: porta alla risposta della FAQ. */
export function trackInstallCardOsChosen(os: string): void {
  track('install-card-os-chosen', { os });
}

export function trackInstallCardDismissed(): void {
  track('install-card-dismissed');
}

// ── Campagne ──

/** Una volta per visita: i due punti di chiamata possono scattare entrambi. */
let campagnaInviata = false;

/**
 * L'evento che dice da quale email si e' arrivati.
 *
 * Va chiamata dal `quandoPronto` di `loadUmami`, cioe' quando `window.umami`
 * esiste DAVVERO: `track()` esce in silenzio se non lo trova, e chiamarla
 * subito dopo l'iniezione dello script la manderebbe nel vuoto mentre il file
 * si sta ancora scaricando.
 *
 * Perche' un evento e non le `utm_*` nell'indirizzo della visita: l'app
 * riscrive il proprio indirizzo al primo effetto (vedi `campagna.ts`), quindi
 * quando lo script esegue la coda `?utm_…` non c'e' piu'. In piu' le visite
 * viaggiano senza stringa di ricerca per scelta (`data-exclude-search` in
 * `load-umami.ts`), a protezione dei gettoni dei link profondi: quella riga
 * resta dov'e'.
 *
 * ⚠️ Questo evento conta solo chi ha ACCETTATO i cookie: e' un minimo, non il
 * totale dei clic. Il totale lo danno i contatori aggregati lato server
 * (`metriche_clic`, report `clic_campagne`), che non hanno bisogno di consenso.
 */
export function inviaCampagnaSePresente(): void {
  if (campagnaInviata) return;
  const campagna = getCampagna();
  if (!campagna) return;
  campagnaInviata = true;
  track('campagna', { ...campagna });
}

/** Solo per i test: rende richiamabile l'evento «una volta sola». */
export function azzeraCampagnaInviata(): void {
  campagnaInviata = false;
}

// ── Canali partner (pagine regole) ──

/**
 * Un clic verso un canale YouTube consigliato, dalla pagina regole di una
 * variante.
 *
 * E' il numero che un programma partner deve poter mostrare ai partner: quanti
 * lettori di QUELLA pagina sono passati di la'. Il canale viaggia col suo id
 * stabile e non con l'handle, che puo' cambiare senza che il canale cambi.
 *
 * ⚠️ Conta solo chi ha accettato i cookie, come ogni evento personalizzato:
 * e' un minimo, non il totale dei clic.
 */
export function trackPartnerChannelClick(channelId: string, variant: string): void {
  track('partner-channel-click', { channelId, ...variantProps(variant) });
}
